import { useEffect, useRef, useState } from 'react';
import type { MailAccountView } from '@epasu/core';
import { crm } from '@/lib/crm-client';

/**
 * Die Absenderauswahl, samt ihrer Vorbelegung.
 *
 * Beides steht hier zusammen, weil es zusammengehört und in allen vier
 * Sende-Dialogen gebraucht wird — dasselbe Muster wie bei `ErstanspracheHaken`
 * und `AnhangWahl`.
 */

export interface Absender {
  /** Nur die aktiven Konten: aus stillgelegten geht nichts raus. */
  konten: MailAccountView[];
  /** Erst wahr, wenn die Abfrage durch ist — vorher ist `konten` leer, ohne dass das etwas hieße. */
  geladen: boolean;
  /** Das gewählte Konto; null, solange nichts geladen ist. */
  konto: string | null;
  setKonto: (id: string) => void;
}

/**
 * Lädt die aktiven Postfächer und belegt die Auswahl vor.
 *
 * `vorgabe` ist das Konto, aus dem der Vorgang bisher lief — bei einer Antwort
 * das der beantworteten Nachricht. Sie darf nachträglich hereinkommen: der
 * FollowupDialog kennt sie erst, wenn der Entwurf da ist. Ist keine da oder
 * gehört sie zu einem stillgelegten Konto, greift das Standardkonto.
 *
 * Sobald jemand von Hand gewählt hat, bleibt es dabei — auch wenn danach noch
 * eine Vorgabe eintrifft. Eine Vorbelegung, die eine bewusste Wahl hinterrücks
 * zurückdreht, wäre genau der Fehler, den niemand bemerkt, bis die Mail raus
 * ist.
 */
export function useAktivePostfaecher(vorgabe?: string | null): Absender {
  const [konten, setKonten] = useState<MailAccountView[]>([]);
  const [geladen, setGeladen] = useState(false);
  const [konto, setKonto] = useState<string | null>(null);
  const vonHand = useRef(false);

  useEffect(() => {
    let abgebrochen = false;
    void crm.mailAccounts.list().then(
      (alle) => {
        if (abgebrochen) return;
        setKonten(alle.filter((k) => k.active));
        setGeladen(true);
      },
      () => {
        // Scheitert die Abfrage, bleibt `geladen` falsch. Das ist Absicht: eine
        // leere Liste hiesse sonst „kein Postfach eingerichtet“, und diese
        // Warnung wegen einer kurz gestoerten Verbindung zu zeigen, waere
        // schlimmer als sie wegzulassen. Die Auswahl bleibt weg, und der Server
        // nimmt ohne `accountId` ohnehin das richtige Konto.
      },
    );
    return () => {
      abgebrochen = true;
    };
  }, []);

  useEffect(() => {
    if (vonHand.current || konten.length === 0) return;
    const gewuenscht = konten.find((k) => k.id === vorgabe);
    const standard = konten.find((k) => k.isDefault);
    setKonto(gewuenscht?.id ?? standard?.id ?? konten[0]?.id ?? null);
  }, [konten, vorgabe]);

  return {
    konten,
    geladen,
    konto,
    setKonto: (id: string) => {
      vonHand.current = true;
      setKonto(id);
    },
  };
}

/**
 * Aus welchem Postfach die Mail rausgeht.
 *
 * Gibt es höchstens ein aktives Postfach, steht hier nichts: Wer nur eins hat,
 * soll keine Auswahl sehen, die keine ist — das ist der Normalfall und soll
 * bleiben, wie er war.
 */
export function PostfachWahl({
  konten,
  wert,
  onChange,
}: {
  konten: MailAccountView[];
  wert: string | null;
  onChange: (id: string) => void;
}) {
  if (konten.length <= 1) return null;

  return (
    <label className="block space-y-1.5">
      <span className="text-xs font-medium text-muted-foreground">Absender</span>
      <select
        className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
        value={wert ?? ''}
        onChange={(e) => onChange(e.target.value)}
      >
        {konten.map((k) => (
          <option key={k.id} value={k.id}>
            {k.label} — {k.fromAddress}
          </option>
        ))}
      </select>
    </label>
  );
}
